import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { IconMenu } from "../icons";
import { IconButton } from "./Button";

interface DrawerControl {
  /** True when the shell has a drawer (always; the menu button only shows under 760 px). */
  available: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  drawerId: string;
}

const DrawerContext = createContext<DrawerControl>({ available: false, open: false, setOpen: () => undefined, drawerId: "" });

/** The shell's navigation drawer (under 760 px). TopBar uses it for its menu button. */
export function useShellDrawer(): DrawerControl {
  return useContext(DrawerContext);
}

export interface AppShellProps {
  /** Sidebar content (usually a SideNav). Shown at 248 px, or in the drawer under 760 px. */
  sidebar: ReactNode;
  /** The 64 px top bar (usually a TopBar). */
  topBar?: ReactNode;
  children: ReactNode;
  /** Accessible name of the content area. */
  mainLabel?: string;
  /** Changing this closes the drawer and moves focus to the content (call it with the current path). */
  locationKey?: string;
}

/**
 * The Hub frame from the approved mockups: a 248 px sidebar with a hairline, a 64 px top bar and the content area.
 * Under 760 px the sidebar collapses into a drawer opened by the top bar's menu button. Includes a skip link.
 */
export function AppShell({ sidebar, topBar, children, mainLabel = "Content", locationKey }: AppShellProps) {
  const [open, setOpenState] = useState(false);
  const drawerId = useId();
  const mainRef = useRef<HTMLElement>(null);
  const setOpen = useCallback((v: boolean) => setOpenState(v), []);
  const ctx = useMemo(() => ({ available: true, open, setOpen, drawerId }), [open, setOpen, drawerId]);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setOpenState(false);
    mainRef.current?.focus();
  }, [locationKey]);

  return (
    <DrawerContext.Provider value={ctx}>
      <div className="lcc-root lcc-shell">
        <a className="lcc-skip" href="#lcc-main" onClick={(e) => { e.preventDefault(); mainRef.current?.focus(); }}>
          Skip to content
        </a>
        <aside className="lcc-shell__side" aria-label="Sidebar">
          {sidebar}
        </aside>
        <div className="lcc-shell__body">
          {topBar}
          <main id="lcc-main" ref={mainRef} tabIndex={-1} aria-label={mainLabel} className="lcc-shell__main">
            {children}
          </main>
        </div>
        {open && <Drawer id={drawerId} onClose={() => setOpenState(false)}>{sidebar}</Drawer>}
      </div>
    </DrawerContext.Provider>
  );
}

function Drawer({ id, onClose, children }: { id: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>("a, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      before?.focus?.();
    };
  }, []);
  return (
    <div className="lcc-drawer">
      <button type="button" className="lcc-drawer__scrim" aria-label="Close the menu" tabIndex={-1} onClick={onClose} />
      <div id={id} ref={ref} className="lcc-drawer__panel" role="dialog" aria-modal="true" aria-label="Menu">
        {children}
      </div>
    </div>
  );
}

/** The menu button that opens the drawer; only visible under 760 px (CSS). */
export function DrawerButton() {
  const d = useShellDrawer();
  if (!d.available) return null;
  return (
    <IconButton
      className="lcc-shell__menu"
      label="Menu"
      icon={<IconMenu size={20} />}
      aria-expanded={d.open}
      aria-controls={d.drawerId}
      onClick={() => d.setOpen(!d.open)}
    />
  );
}
